import React, { useState, useEffect } from 'react';
import {
  Heart,
  Star,
  MapPin,
  Filter,
  TrendingUp,
  Users,
  Clock,
  Eye,
  ChevronRight,
  Sliders,
  X,
  TestTube
} from 'lucide-react';
import { Business, User, Review, UserInterest, PriceRange, UserPreferences } from '../types';
import FilteringEngine, { PersonalizedFeed as PersonalizedFeedType } from '../utils/filteringEngine';
import FilteringTestPanel from './FilteringTestPanel';

interface PersonalizedFeedProps {
  user: User;
  businesses: Business[];
  reviews: Review[];
  onBusinessSelect: (business: Business) => void;
  onUpdateInterests: () => void;
  onUpdatePreferences: (preferences: UserPreferences) => void;
}

const PersonalizedFeed: React.FC<PersonalizedFeedProps> = ({
  user,
  businesses,
  reviews,
  onBusinessSelect,
  onUpdateInterests,
  onUpdatePreferences,
}) => {
  const [feed, setFeed] = useState<PersonalizedFeedType | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showTestPanel, setShowTestPanel] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    maxDistance: user.preferences?.maxDistance || 25,
    minimumRating: user.preferences?.minimumRating || 3.0,
    priceRange: user.preferences?.priceRange || [],
    showVerifiedOnly: user.preferences?.showVerifiedOnly || false,
    notificationSettings: {
      newReviews: user.preferences?.notificationSettings.newReviews || false,
      businessUpdates: user.preferences?.notificationSettings.businessUpdates || false,
      socialShares: user.preferences?.notificationSettings.socialShares || false,
      weeklyDigest: user.preferences?.notificationSettings.weeklyDigest || false,
    }
  });

  useEffect(() => {
    generateFeed();
  }, [user.interests, businesses, reviews, filterOptions]);

  const generateFeed = () => {
    const personalizedFeed = FilteringEngine.generatePersonalizedFeed(
      businesses,
      reviews,
      {
        userInterests: user.interests || [],
        ...filterOptions
      }
    );
    setFeed(personalizedFeed);
  };

  const renderStars = (rating: number, size = 'w-4 h-4') => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`${size} ${i < Math.floor(rating)
          ? 'text-yellow-400 fill-current'
          : 'text-gray-300'
          }`}
      />
    ));
  };

  const getRecommendationQuality = (score: number) => {
    if (score >= 8) return { text: 'Excellent Match', color: 'text-green-600', bg: 'bg-green-100' };
    if (score >= 6) return { text: 'Great Match', color: 'text-blue-600', bg: 'bg-blue-100' };
    if (score >= 4) return { text: 'Good Match', color: 'text-yellow-600', bg: 'bg-yellow-100' };
    return { text: 'Fair Match', color: 'text-gray-600', bg: 'bg-gray-100' };
  };

  if (!feed) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const userAnalysis = FilteringEngine.analyzeUserInterests(user.interests || []);
  const quality = getRecommendationQuality(feed.recommendationScore);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Your Personalized Feed</h2>
          <p className="text-sm text-gray-600 mt-1">
            Based on your interests and preferences
          </p>
        </div>
        <div className="flex md:items-center gap-x-4">
          {/* <button
            onClick={() => setShowTestPanel(true)}
            className="flex items-center space-x-2 px-3 py-2 text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded-lg transition-colors duration-200"
          >
            <TestTube className="w-4 h-4" />
            <span className="text-sm">Test Filtering</span>
          </button> */}
          <button
            onClick={() => setShowFilters(true)}
            className="flex items-center gap-x-2 px-2 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors duration-200"
          >
            <Filter className="w-4 h-4" />
            <span className="text-sm">Filters</span>
          </button>
          <button
            onClick={onUpdateInterests}
            className="flex items-center gap-x-2 px-2 py-2 text-accent-600 hover:text-accent-700 hover:bg-accent-50 rounded-lg transition-colors duration-200"
          >
            <Sliders className="w-4 h-4" />
            <span className="text-sm">Update Interests</span>
          </button>
        </div>
      </div>

      {/* Recommendation Quality */}
      {/* <div className={`${quality.bg} border border-current border-opacity-20 rounded-lg p-4`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <TrendingUp className={`w-5 h-5 ${quality.color}`} />
            <div>
              <span className={`font-medium ${quality.color}`}>{quality.text}</span>
              <p className="text-sm text-gray-600 mt-1">
                {feed.matchedInterests.length > 0 
                  ? `Matching ${feed.matchedInterests.slice(0, 3).join(', ')}${feed.matchedInterests.length > 3 ? ' and more' : ''}`
                  : 'Add more interests for better recommendations'
                }
              </p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-lg font-semibold text-gray-900">
              {feed.recommendationScore.toFixed(1)}/10
            </div>
            <div className="text-xs text-gray-500">Match Score</div>
          </div>
        </div>
      </div> */}

      {/* Interest Analysis */}
      {userAnalysis.topCategories.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="font-medium text-gray-900 mb-3">Your Top Interests</h3>
          <div className="flex flex-wrap gap-2 mb-3">
            {userAnalysis.topCategories.map((category) => (
              <span
                key={category}
                className="px-3 py-1 bg-primary-100 text-primary-800 text-sm rounded-full"
              >
                {category.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </span>
            ))}
          </div>
          {userAnalysis.recommendations.length > 0 && (
            <div className="text-sm text-gray-600">
              💡 {userAnalysis.recommendations[0]}
            </div>
          )}
        </div>
      )}

      {/* Recommended Businesses */}
      <div>
        <h3 className="font-medium text-gray-900 mb-4">Recommended for You</h3>
        {feed.businesses.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {feed.businesses.slice(0, 6).map((business) => {
              const relevanceScore = (business as any).relevanceScore || 0;
              const matchQuality = getRecommendationQuality(relevanceScore);

              return (
                <div
                  key={business.id}
                  className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow duration-200 cursor-pointer"
                  onClick={() => onBusinessSelect(business)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900 mb-1">{business.name}</h4>
                      <p className="text-sm text-gray-600">{business.category}</p>
                    </div>
                    <div className={`px-2 py-1 rounded-full text-xs font-medium ${matchQuality.bg} ${matchQuality.color}`}>
                      {relevanceScore.toFixed(1)}
                    </div>
                  </div>

                  <div className="flex items-center space-x-4 mb-3">
                    <div className="flex items-center space-x-1">
                      {renderStars(business.averageRating || 0)}
                      <span className="text-sm text-gray-600 ml-1">
                        ({business.totalReviews})
                      </span>
                    </div>
                    {business.priceRange && (
                      <span className="text-sm text-gray-600">{business.priceRange}</span>
                    )}
                  </div>

                  <div className="flex items-center space-x-2 text-sm text-gray-600 mb-3">
                    <MapPin className="w-3 h-3" />
                    <span className="truncate">{business.address}</span>
                  </div>

                  {business.description && (
                    <p className="text-sm text-gray-700 line-clamp-2 mb-3">
                      {business.description}
                    </p>
                  )}

                  <div className="flex items-center justify-between">
                    <div className="flex flex-wrap gap-1">
                      {business.tags?.slice(0, 2).map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Eye className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No recommendations yet</h3>
            <p className="text-gray-600 mb-4">
              Add some interests to get personalized business recommendations
            </p>
            <button
              onClick={onUpdateInterests}
              className="btn-primary"
            >
              Add Interests
            </button>
          </div>
        )}
      </div>

      {/* Recent Reviews */}
      {feed.reviews.length > 0 && (
        <div>
          <h3 className="font-medium text-gray-900 mb-4">Recent Reviews in Your Interests</h3>
          <div className="space-y-4">
            {feed.reviews.slice(0, 3).map((review) => {
              const business = businesses.find(b => b.id === review.businessId);
              if (!business) return null;

              return (
                <div key={review.id} className="bg-white rounded-lg border border-gray-200 p-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-10 h-10 bg-accent-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-sm font-medium">
                        {review.userName.charAt(0)}
                      </span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="font-medium text-gray-900">{review.userName}</span>
                        {review.userVerified && (
                          <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                            <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                        <div className="flex items-center">
                          {renderStars(review.rating)}
                        </div>
                        <span className="text-sm text-gray-500">•</span>
                        <span className="text-sm text-gray-500">
                          <Clock className="w-3 h-3 inline mr-1" />
                          {new Date(review.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                      <h4 className="font-medium text-gray-900 mb-1">{review.title}</h4>
                      <p className="text-sm text-gray-600 mb-2">
                        Review for <span className="font-medium">{business.name}</span>
                      </p>
                      <p className="text-sm text-gray-700 line-clamp-2">{review.content}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Filter Modal */}
      {showFilters && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Filter Options</h3>
              <button
                onClick={() => setShowFilters(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Maximum Distance: {filterOptions.maxDistance} miles
                </label>
                <input
                  type="range"
                  min="1"
                  max="50"
                  value={filterOptions.maxDistance}
                  onChange={(e) => setFilterOptions(prev => ({ ...prev, maxDistance: parseInt(e.target.value) }))}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Minimum Rating: {filterOptions.minimumRating.toFixed(1)}
                </label>
                <input
                  type="range"
                  min="1"
                  max="5"
                  step="0.1"
                  value={filterOptions.minimumRating}
                  onChange={(e) => setFilterOptions(prev => ({ ...prev, minimumRating: parseFloat(e.target.value) }))}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Price Range
                </label>
                <div className="flex space-x-2">
                  {(['$', '$$', '$$$', '$$$$'] as PriceRange[]).map((price) => (
                    <button
                      key={price}
                      onClick={() => {
                        const newPriceRange = filterOptions.priceRange.includes(price)
                          ? filterOptions.priceRange.filter(p => p !== price)
                          : [...filterOptions.priceRange, price];
                        setFilterOptions(prev => ({ ...prev, priceRange: newPriceRange }));
                      }}
                      className={`px-3 py-2 rounded-lg border transition-colors duration-200 ${filterOptions.priceRange.includes(price)
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                      {price}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="verifiedOnly"
                  checked={filterOptions.showVerifiedOnly}
                  onChange={(e) => setFilterOptions(prev => ({ ...prev, showVerifiedOnly: e.target.checked }))}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="verifiedOnly" className="text-sm text-gray-700">
                  Show only verified businesses
                </label>
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setShowFilters(false)}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  generateFeed();
                  setShowFilters(false);
                  onUpdatePreferences(filterOptions);
                }}
                className="btn-primary flex-1"
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filtering Test Panel */}
      {showTestPanel && (
        <FilteringTestPanel
          isOpen={showTestPanel}
          onClose={() => setShowTestPanel(false)}
          businesses={businesses}
          user={user}
        />
      )}
    </div>
  );
};

export default PersonalizedFeed;
